import {
  Button,
  ButtonGroup,
  Divider,
  FormControl,
  FormLabel,
  HStack,
  IconButton,
  Input,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverCloseButton,
  PopoverContent,
  PopoverFooter,
  PopoverHeader,
  PopoverTrigger,
} from '@chakra-ui/react';
import { Editor } from '@tiptap/core';
import { useEditorState } from '@tiptap/react';
import { memo, useState } from 'react';
import {
  MdCode,
  MdFormatAlignCenter,
  MdFormatAlignLeft,
  MdFormatAlignRight,
  MdFormatBold,
  MdFormatItalic,
  MdFormatLineSpacing,
  MdFormatListBulleted,
  MdFormatListNumbered,
  MdFormatQuote,
  MdFormatUnderlined,
  MdInsertLink,
  MdRedo,
  MdUndo,
} from 'react-icons/md';

interface TipTapToolbarProps {
  editor: Editor;
  editable: boolean;
  isStandalone?: boolean; // Whether toolbar is rendered separately from editor
}

/**
 * Toolbar component for TipTap editor
 * Provides formatting controls for rich text editing
 */
export const TipTapToolbar = memo(function TipTapToolbar({
  editor,
  editable,
  isStandalone = false,
}: TipTapToolbarProps) {
  const [isLinkPopoverOpen, setIsLinkPopoverOpen] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');

  // TipTap v3's `useEditor` no longer re-renders on every transaction, so the
  // toolbar subscribes to the editor state itself to keep the formatting
  // buttons in sync with the current selection. The selector result is
  // deep-compared, so transactions that don't change any of these flags (e.g.
  // plain typing) skip the re-render.
  const state = useEditorState({
    editor,
    selector: ({ editor: e }) => ({
      isBold: e.isActive('bold'),
      isItalic: e.isActive('italic'),
      isUnderline: e.isActive('underline'),
      isLink: e.isActive('link'),
      isBulletList: e.isActive('bulletList'),
      isOrderedList: e.isActive('orderedList'),
      isAlignLeft: e.isActive({ textAlign: 'left' }),
      isAlignCenter: e.isActive({ textAlign: 'center' }),
      isAlignRight: e.isActive({ textAlign: 'right' }),
      // Get current line height from paragraph or heading
      paragraphLineHeight: e.getAttributes('paragraph').lineHeight,
      headingLineHeight: e.getAttributes('heading').lineHeight,
      isBlockquote: e.isActive('blockquote'),
      isCode: e.isActive('code'),
      canUndo: e.can().undo(),
      canRedo: e.can().redo(),
    }),
  });

  const handleSetLink = () => {
    if (!linkUrl) {
      // Remove link if URL is empty
      editor.chain().focus().unsetLink().run();
    } else {
      // Validate URL format (basic)
      let url = linkUrl.trim();
      if (url && !url.match(/^https?:\/\//i)) {
        // Add https:// if no protocol specified
        url = 'https://' + url;
      }

      // Set the link and move cursor outside of it
      editor
        .chain()
        .focus()
        .extendMarkRange('link') // Extend selection to full link if cursor is inside one
        .setLink({ href: url })
        .setTextSelection(editor.state.selection.to) // Move cursor to end of selection
        .insertContent(' ') // Insert space to exit link mark
        .deleteRange({
          from: editor.state.selection.from - 1,
          to: editor.state.selection.from,
        }) // Remove the space
        .run();
    }

    setIsLinkPopoverOpen(false);
    setLinkUrl('');
  };

  const handleRemoveLink = () => {
    editor.chain().focus().unsetLink().run();
    setIsLinkPopoverOpen(false);
    setLinkUrl('');
  };

  if (!editable) {
    return null;
  }

  return (
    <HStack
      p={2}
      {...(isStandalone
        ? {
            // When standalone, toolbar has full border except bottom, with top rounded corners
            border: '1px solid',
            borderColor: 'gray.200',
            borderBottom: 'none',
            borderTopRadius: 'md',
            zIndex: 10,
          }
        : {
            // When embedded, toolbar only has bottom border and top rounded corners (parent provides outer border)
            borderBottom: '1px solid',
            borderColor: 'gray.200',
            borderTopRadius: 'md',
            zIndex: 5,
          })}
      bg="bg.subtle"
      spacing={1}
      flexWrap="wrap"
      position="sticky"
      top={0}
    >
      <ButtonGroup size="sm" isAttached variant="outline">
        <IconButton
          aria-label="Bold"
          icon={<MdFormatBold />}
          onClick={() => editor.chain().focus().toggleBold().run()}
          isActive={state.isBold}
          variant={state.isBold ? 'solid' : 'outline'}
          colorScheme="green"
        />
        <IconButton
          aria-label="Italic"
          icon={<MdFormatItalic />}
          onClick={() => editor.chain().focus().toggleItalic().run()}
          isActive={state.isItalic}
          variant={state.isItalic ? 'solid' : 'outline'}
          colorScheme="green"
        />
        <IconButton
          aria-label="Underline"
          icon={<MdFormatUnderlined />}
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          isActive={state.isUnderline}
          variant={state.isUnderline ? 'solid' : 'outline'}
          colorScheme="green"
        />
      </ButtonGroup>

      <Divider orientation="vertical" h="24px" />

      <Popover
        isOpen={isLinkPopoverOpen}
        onClose={() => {
          setIsLinkPopoverOpen(false);
          setLinkUrl('');
        }}
        placement="bottom-start"
        closeOnBlur={true}
      >
        <PopoverTrigger>
          <IconButton
            aria-label="Insert Link"
            icon={<MdInsertLink />}
            onClick={() => {
              const previousUrl = editor.getAttributes('link').href || '';
              setLinkUrl(previousUrl);
              setIsLinkPopoverOpen(true);
            }}
            isActive={state.isLink}
            variant={state.isLink ? 'solid' : 'outline'}
            colorScheme="green"
            size="sm"
          />
        </PopoverTrigger>
        <PopoverContent>
          <PopoverArrow />
          <PopoverCloseButton />
          <PopoverHeader fontWeight="bold">
            {state.isLink ? 'Edit Link' : 'Insert Link'}
          </PopoverHeader>
          <PopoverBody>
            <FormControl>
              <FormLabel fontSize="sm">URL</FormLabel>
              <Input
                placeholder="https://example.com"
                value={linkUrl}
                onChange={(e) => setLinkUrl(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleSetLink();
                  }
                }}
                size="sm"
                autoFocus
              />
            </FormControl>
          </PopoverBody>
          <PopoverFooter display="flex" justifyContent="space-between">
            {state.isLink && (
              <Button
                size="sm"
                variant="ghost"
                colorScheme="red"
                onClick={handleRemoveLink}
              >
                Remove Link
              </Button>
            )}
            <Button
              size="sm"
              colorScheme="green"
              onClick={handleSetLink}
              ml="auto"
            >
              {state.isLink ? 'Update' : 'Insert'}
            </Button>
          </PopoverFooter>
        </PopoverContent>
      </Popover>

      <Divider orientation="vertical" h="24px" />

      <ButtonGroup size="sm" isAttached variant="outline">
        <IconButton
          aria-label="Bullet List"
          icon={<MdFormatListBulleted />}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          isActive={state.isBulletList}
          variant={state.isBulletList ? 'solid' : 'outline'}
          colorScheme="green"
        />
        <IconButton
          aria-label="Numbered List"
          icon={<MdFormatListNumbered />}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          isActive={state.isOrderedList}
          variant={state.isOrderedList ? 'solid' : 'outline'}
          colorScheme="green"
        />
      </ButtonGroup>

      <Divider orientation="vertical" h="24px" />

      <ButtonGroup size="sm" isAttached variant="outline">
        <IconButton
          aria-label="Align Left"
          icon={<MdFormatAlignLeft />}
          onClick={() => editor.chain().focus().setTextAlign('left').run()}
          isActive={state.isAlignLeft}
          variant={state.isAlignLeft ? 'solid' : 'outline'}
          colorScheme="green"
        />
        <IconButton
          aria-label="Align Center"
          icon={<MdFormatAlignCenter />}
          onClick={() => editor.chain().focus().setTextAlign('center').run()}
          isActive={state.isAlignCenter}
          variant={state.isAlignCenter ? 'solid' : 'outline'}
          colorScheme="green"
        />
        <IconButton
          aria-label="Align Right"
          icon={<MdFormatAlignRight />}
          onClick={() => editor.chain().focus().setTextAlign('right').run()}
          isActive={state.isAlignRight}
          variant={state.isAlignRight ? 'solid' : 'outline'}
          colorScheme="green"
        />
      </ButtonGroup>

      <Divider orientation="vertical" h="24px" />

      <Menu>
        <MenuButton
          as={Button}
          aria-label="Line Spacing"
          leftIcon={<MdFormatLineSpacing />}
          size="sm"
          variant="outline"
          colorScheme="green"
          fontWeight="normal"
        >
          {state.paragraphLineHeight || state.headingLineHeight || '1.15'}
        </MenuButton>
        <MenuList minW="140px">
          <MenuItem
            onClick={() => editor.chain().focus().setLineHeight('1').run()}
            fontSize="sm"
            bg={
              state.paragraphLineHeight === '1' ||
              state.headingLineHeight === '1'
                ? 'green.50'
                : undefined
            }
            fontWeight={
              state.paragraphLineHeight === '1' ||
              state.headingLineHeight === '1'
                ? 'semibold'
                : 'normal'
            }
          >
            Single (1.0)
          </MenuItem>
          <MenuItem
            onClick={() => editor.chain().focus().setLineHeight('1.15').run()}
            fontSize="sm"
            bg={
              state.paragraphLineHeight === '1.15' ||
              state.headingLineHeight === '1.15'
                ? 'green.50'
                : undefined
            }
            fontWeight={
              state.paragraphLineHeight === '1.15' ||
              state.headingLineHeight === '1.15'
                ? 'semibold'
                : 'normal'
            }
          >
            1.15
          </MenuItem>
          <MenuItem
            onClick={() => editor.chain().focus().setLineHeight('1.5').run()}
            fontSize="sm"
            bg={
              state.paragraphLineHeight === '1.5' ||
              state.headingLineHeight === '1.5'
                ? 'green.50'
                : undefined
            }
            fontWeight={
              state.paragraphLineHeight === '1.5' ||
              state.headingLineHeight === '1.5'
                ? 'semibold'
                : 'normal'
            }
          >
            1.5
          </MenuItem>
          <MenuItem
            onClick={() => editor.chain().focus().setLineHeight('2').run()}
            fontSize="sm"
            bg={
              state.paragraphLineHeight === '2' ||
              state.headingLineHeight === '2'
                ? 'green.50'
                : undefined
            }
            fontWeight={
              state.paragraphLineHeight === '2' ||
              state.headingLineHeight === '2'
                ? 'semibold'
                : 'normal'
            }
          >
            Double (2.0)
          </MenuItem>
        </MenuList>
      </Menu>

      <Divider orientation="vertical" h="24px" />

      <ButtonGroup size="sm" isAttached variant="outline">
        <IconButton
          aria-label="Blockquote"
          icon={<MdFormatQuote />}
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
          isActive={state.isBlockquote}
          variant={state.isBlockquote ? 'solid' : 'outline'}
          colorScheme="green"
        />
        <IconButton
          aria-label="Code"
          icon={<MdCode />}
          onClick={() => editor.chain().focus().toggleCode().run()}
          isActive={state.isCode}
          variant={state.isCode ? 'solid' : 'outline'}
          colorScheme="green"
        />
      </ButtonGroup>

      <Divider orientation="vertical" h="24px" />

      <ButtonGroup size="sm" isAttached variant="outline">
        <IconButton
          aria-label="Undo"
          icon={<MdUndo />}
          onClick={() => editor.chain().focus().undo().run()}
          isDisabled={!state.canUndo}
          colorScheme="green"
        />
        <IconButton
          aria-label="Redo"
          icon={<MdRedo />}
          onClick={() => editor.chain().focus().redo().run()}
          isDisabled={!state.canRedo}
          colorScheme="green"
        />
      </ButtonGroup>
    </HStack>
  );
});
